/**
 * ActionRow (BUILD_SPEC.md §10.3).
 *
 * One row in the action history shown on a TransactionCard. Layout:
 *   • Department on top (small, muted)
 *   • Status pill + final action text + date on a single line
 *   • Final action goes through KeywordHighlight for §10.4 styling
 *
 * Every Arabic field gets `rtl-isolate` so it renders correctly even
 * when the surrounding chrome is in an LTR layout.
 */

import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { KeywordHighlight } from "./KeywordHighlight";
import type { Action } from "@/api/types";
import { detectFlashKeyword, type FlashKeywordType } from "../../../../packages/keywords/index.js";
import { formatActionDate } from "@/lib/dates";

interface Props {
  action: Action;
}

/**
 * v1.1 B8 / v1.1.1 — filled-circle keyword icon, rendered at the START
 * of an action row when the client-side flash classifier matches the
 * action's final-action text. Uses detectFlashKeyword (exact-token rule,
 * 2026-05-14), so only bare "موافقة" (APPROVAL) and the pair "عدم
 * موافقة" (REJECTION) show an icon. ASSIGNMENT (تكليف) no longer shows
 * an icon in v1.1; the takleef flip in B5 is the only assignment
 * indicator on the card.
 */
function KeywordIcon({ type }: { type: FlashKeywordType }) {
  const meta = {
    APPROVAL: { Icon: Check, bg: "bg-approval dark:bg-approval-dark" },
    REJECTION: { Icon: X, bg: "bg-rejection dark:bg-rejection-dark" },
  }[type];
  return (
    <span
      aria-label={type}
      title={type}
      // 28px diameter — slightly larger than the surrounding body
      // text per spec, comfortable at 2-3 m TV distance.
      className={`shrink-0 inline-flex items-center justify-center rounded-lg h-7 w-7 ${meta.bg}`}
    >
      <meta.Icon className="h-4 w-4 text-canvas dark:text-canvas-dark" aria-hidden strokeWidth={3} />
    </span>
  );
}

export function ActionRow({ action }: Props) {
  const { t } = useTranslation();
  const flashKind = detectFlashKeyword(action.finalAction);
  return (
    <div className="border-t border-hairline dark:border-hairline-dark py-2 first:border-t-0 first:pt-0 flex items-start gap-2">
      {flashKind ? <KeywordIcon type={flashKind} /> : null}
      <div className="flex-1 min-w-0">
        <div className="rtl-isolate text-xs text-muted dark:text-muted-dark mb-1">
          {action.department}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rtl-isolate inline-flex items-center rounded-full bg-hairline/40 dark:bg-hairline-dark/60 px-2 py-0.5 text-xs font-medium text-fg dark:text-fg-dark">
            {action.status}
          </span>
          <span className="text-xs text-muted dark:text-muted-dark">
            {formatActionDate(action.actionDate)}
          </span>
        </div>
        {action.finalAction ? (
          <p className="rtl-isolate mt-1 text-sm leading-relaxed text-fg dark:text-fg-dark">
            <KeywordHighlight text={action.finalAction} />
          </p>
        ) : (
          <p className="mt-1 text-xs italic text-muted dark:text-muted-dark">{t("dashboard.noActions")}</p>
        )}
      </div>
    </div>
  );
}
